import { Dynamic } from "solid-js/web";
import { cn } from "@bso/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the large wordmark. Defaults to "h1". Pass "p" when the
   * logo is decorative and the page heading already lives elsewhere (e.g. the
   * auth brand surface, where AuthCard owns the single page h1).
   */
  as?: "h1" | "p";
}

/**
 * Marca de Bible Strong Offline.
 *
 * Glyph is an open book with a cross rising from central spine:
 * "Escritura + estudio" — erudito, reverente y propio. El mark vive SIEMPRE
 * on a solid chip (`bg-accent` + `text-accent-foreground`), never on a
 * faint tint: guaranteed contrast across EVERY bundled theme
 * (light with saturated accents, dark, sepia, etc.). Wordmark uses serif
 * de marca (EB Garamond) SIEMPRE en `text-foreground` (contraste garantizado en
 * all themes) and distinguishes "Strong" only by font weight (bold), not color: never
 * falls below AA threshold as `text-accent` would in themes like Carbon or
 * Royal Velvet (accent ~2.5:1 over background). Brand color provided by
 * solid chip of the glyph.
 */
function BibleMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* Cruz que se eleva sobre el libro */}
      <path
        d="M12 2.4v4.2M10.3 4h3.4"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
      />
      {/* Libro abierto */}
      <path
        d="M12 9.1C10.3 7.9 8 7.5 5.4 7.8c-.5 0-.8.4-.8.9v9.1c0 .5.4.9.9.8 2.3-.2 4.4.2 5.9 1.3.3.2.7.2 1 0 1.5-1.1 3.6-1.5 5.9-1.3.5 0 .9-.3.9-.8V8.7c0-.5-.4-.9-.8-.9-2.6-.3-4.9.1-6.5 1.3Z"
        fill="currentColor"
        fill-opacity="0.18"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      {/* Lomo / canal central */}
      <path d="M12 9.1v11.7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      {/* Renglones de escritura (páginas) */}
      <path
        d="M7 11.4c1.4-.2 2.6 0 3.4.5M7 14c1.4-.2 2.6 0 3.4.5M13.6 11.9c.8-.5 2-.7 3.4-.5M13.6 14.5c.8-.5 2-.7 3.4-.5"
        stroke="currentColor"
        stroke-width="1.1"
        stroke-linecap="round"
        stroke-opacity="0.65"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="h-9 w-9 rounded-xl bg-accent text-accent-foreground shadow-sm flex items-center justify-center shrink-0">
            <BibleMark class="h-5 w-5" />
          </div>
          <span class="font-serif text-xl tracking-tight text-foreground">
            Bible<span class="font-semibold">Strong</span>
          </span>
        </>
      ) : (
        <>
          <div class="h-16 w-16 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center mb-1 shadow-md shrink-0 transition-transform hover:scale-105 active:scale-95 duration-300">
            <BibleMark class="h-9 w-9" />
          </div>
          <Dynamic
            component={props.as ?? "h1"}
            class="font-serif text-3xl tracking-tight text-foreground"
          >
            Bible<span class="font-semibold">Strong</span>
          </Dynamic>
        </>
      )}
    </div>
  );
}
